/**
 * Expectations API service with TanStack React Query hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut, apiDelete, isTokenGetterSet } from './client'
import type {
  PriceExpectation,
  CreatePriceExpectationInput,
  UpdatePriceExpectationInput,
} from '@/types/expectation'

/**
 * Query keys for expectations
 */
export const expectationKeys = {
  all: ['expectations'] as const,
  lists: () => [...expectationKeys.all, 'list'] as const,
  list: () => [...expectationKeys.lists()] as const,
  details: () => [...expectationKeys.all, 'detail'] as const,
  detail: (id: string) => [...expectationKeys.details(), id] as const,
}

/**
 * Fetch all expectations for the current user
 */
export function useExpectations() {
  return useQuery({
    queryKey: expectationKeys.list(),
    queryFn: async () => {
      const response = await apiGet<{ data: PriceExpectation[] }>('/api/expectations')
      return response.data
    },
    enabled: isTokenGetterSet(), // Only run when token getter is set
  })
}

/**
 * Create a new expectation
 */
export function useCreateExpectation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreatePriceExpectationInput) => {
      const response = await apiPost<{ data: PriceExpectation; message: string }>('/api/expectations', input)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expectationKeys.lists() })
    },
  })
}

/**
 * Update an expectation
 */
export function useUpdateExpectation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdatePriceExpectationInput }) => {
      const response = await apiPut<{ data: PriceExpectation; message: string }>(`/api/expectations/${id}`, input)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: expectationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: expectationKeys.detail(data.id) })
    },
  })
}

/**
 * Delete an expectation
 */
export function useDeleteExpectation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await apiDelete<{ message: string }>(`/api/expectations/${id}`)
      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expectationKeys.lists() })
    },
  })
}

